/**
 * Case Detail Page
 * ================
 * Full-page case detail view with tabs for:
 * - Overview: Case info, assignee, SLA metrics
 * - Subject: Linked applicant and their documents
 * - Investigation: Notes and timeline
 * - Tags: Tag management
 */

import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Clock,
  AlertTriangle,
  CheckCircle2,
  FileText,
  MessageSquare,
  Tag,
  Send,
  Plus,
  X,
  Loader2,
  ChevronUp,
  ExternalLink,
  History,
} from 'lucide-react';
import {
  useCase,
  useResolveCase,
  useAddCaseNote,
  useCaseTimeline,
  useCaseTags,
  useAddTagToCase,
  useRemoveTagFromCase,
  useEscalateCase,
} from '../../hooks/useCases';
import { useCaseChecklist } from '../../hooks/usePlaybooks';
import { useToast } from '../../contexts/ToastContext';
import CaseTimeline from './CaseTimeline';
import CaseChecklist from './CaseChecklist';
import AssigneeDropdown from './AssigneeDropdown';
import EscalationModal from './EscalationModal';
import {
  CASE_TYPES as typeConfig,
  CASE_PRIORITIES as priorityConfig,
  CASE_STATUSES as statusConfig,
  CASE_RESOLUTIONS as resolutionConfig,
  formatDateTime,
  formatRelativeTime,
} from '../../config/caseConfig';

const tabs = [
  { id: 'overview', label: 'Overview', icon: FileText },
  { id: 'subject', label: 'Subject', icon: User },
  { id: 'investigation', label: 'Investigation', icon: MessageSquare },
  { id: 'tags', label: 'Tags', icon: Tag },
];

export default function CaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  // Get active tab from URL
  const activeTab = searchParams.get('tab') || 'overview';
  const setActiveTab = (tab) => {
    setSearchParams({ tab }, { replace: true });
  };

  // Local state
  const [noteText, setNoteText] = useState('');
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);

  // Fetch case data (now includes nested applicant, documents, tags, etc.)
  const { data: caseData, isLoading, error } = useCase(id);

  // Fetch timeline
  const { data: timelineData } = useCaseTimeline(id);

  // Fetch checklist for this case
  const { data: checklistData } = useCaseChecklist(id);
  const hasChecklist = checklistData && checklistData.total_items > 0;

  // Fetch all available tags
  const { data: tagsData } = useCaseTags();
  const availableTags = tagsData?.items || [];

  // Mutations
  const resolveCase = useResolveCase();
  const addNote = useAddCaseNote();
  const addTagToCase = useAddTagToCase();
  const removeTagFromCase = useRemoveTagFromCase();
  const escalateCase = useEscalateCase();

  // Handlers
  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    try {
      await addNote.mutateAsync({ id, content: noteText });
      setNoteText('');
      toast.success('Note added');
    } catch (err) {
      toast.error('Failed to add note');
    }
  };

  const handleResolve = async (resolution, notes) => {
    try {
      await resolveCase.mutateAsync({ id, resolution, notes });
      setShowResolveModal(false);
      toast.success('Case resolved');
    } catch (err) {
      toast.error('Failed to resolve case');
    }
  };

  const handleEscalate = async (data) => {
    try {
      await escalateCase.mutateAsync({ id, data });
      setShowEscalateModal(false);
      toast.success('Case escalated successfully');
    } catch (err) {
      toast.error('Failed to escalate case');
    }
  };

  const handleAddTag = async (tagId) => {
    try {
      await addTagToCase.mutateAsync({ caseId: id, tagId });
      toast.success('Tag added');
    } catch (err) {
      toast.error('Failed to add tag');
    }
  };

  const handleRemoveTag = async (tagId) => {
    try {
      await removeTagFromCase.mutateAsync({ caseId: id, tagId });
      toast.success('Tag removed');
    } catch (err) {
      toast.error('Failed to remove tag');
    }
  };

  if (isLoading) {
    return (
      <div className="case-detail-page">
        <div className="loading-state">
          <Loader2 className="spinner" size={32} />
          <p>Loading case...</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="case-detail-page">
        <div className="error-state">
          <AlertTriangle size={48} />
          <h2>Case not found</h2>
          <p>The case you're looking for doesn't exist or you don't have access.</p>
          <button className="btn btn-primary" onClick={() => navigate('/cases')}>
            Back to Cases
          </button>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  const TypeIcon = typeConfig[caseData.type]?.icon || FileText;
  const caseTags = caseData.tags || [];
  const unassignedTags = availableTags.filter(
    (t) => !caseTags.some((ct) => ct.id === t.id)
  );

  return (
    <div className="case-detail-page">
      {/* Header */}
      <header className="case-header">
        <button className="back-btn" onClick={() => navigate('/cases')}>
          <ArrowLeft size={20} />
          Back to Cases
        </button>

        <div className="case-title-row">
          <div className="case-title-info">
            <span className="case-number">{caseData.case_number}</span>
            <h1>{caseData.title}</h1>
            <div className="case-badges">
              <span className={`badge ${typeConfig[caseData.type]?.color || 'muted'}`}>
                <TypeIcon size={14} />
                {typeConfig[caseData.type]?.label || caseData.type}
              </span>
              <span className={`badge ${priorityConfig[caseData.priority]?.color || 'muted'}`}>
                {priorityConfig[caseData.priority]?.label || caseData.priority}
              </span>
              <span className={`badge ${statusConfig[caseData.status]?.color || 'muted'}`}>
                {statusConfig[caseData.status]?.label || caseData.status}
              </span>
              {caseData.is_overdue && (
                <span className="badge danger">
                  <Clock size={14} />
                  Overdue
                </span>
              )}
            </div>
          </div>

          <div className="case-actions-area">
            {/* Assignee Dropdown */}
            <div className="assignee-section">
              <label className="assignee-label">Assignee</label>
              <AssigneeDropdown
                caseId={id}
                currentAssignee={caseData.assignee}
              />
            </div>

            {/* Action Buttons */}
            <div className="case-actions">
              {caseData.status !== 'resolved' && caseData.status !== 'closed' && (
                <>
                  <button
                    className="btn btn-warning"
                    onClick={() => setShowEscalateModal(true)}
                  >
                    <ChevronUp size={16} />
                    Escalate
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowResolveModal(true)}
                  >
                    <CheckCircle2 size={16} />
                    Resolve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <nav className="case-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {/* Tab Content with Optional Checklist Sidebar */}
      <div className={`case-layout ${hasChecklist ? 'with-sidebar' : ''}`}>
        <div className="case-content">
          {activeTab === 'overview' && (
            <OverviewTab caseData={caseData} />
          )}
          {activeTab === 'subject' && (
            <SubjectTab caseData={caseData} />
          )}
          {activeTab === 'investigation' && (
            <InvestigationTab
              caseData={caseData}
              timeline={timelineData}
              noteText={noteText}
              setNoteText={setNoteText}
              onAddNote={handleAddNote}
              isAddingNote={addNote.isPending}
            />
          )}
          {activeTab === 'tags' && (
            <TagsTab
              caseTags={caseTags}
              availableTags={unassignedTags}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
            />
          )}
        </div>

        {/* Checklist Sidebar */}
        {hasChecklist && (
          <div className="case-sidebar">
            <CaseChecklist caseId={id} showWarning={caseData?.status !== 'resolved'} />
          </div>
        )}
      </div>

      {/* Resolve Modal */}
      {showResolveModal && (
        <ResolveModal
          onClose={() => setShowResolveModal(false)}
          onResolve={handleResolve}
          isLoading={resolveCase.isPending}
        />
      )}

      {showEscalateModal && (
        <EscalationModal
          isOpen={showEscalateModal}
          onClose={() => setShowEscalateModal(false)}
          onEscalate={handleEscalate}
          isLoading={escalateCase.isPending}
          currentEscalationLevel={caseData.escalation_level || 0}
          currentPriority={caseData.priority}
        />
      )}

      <style>{styles}</style>
    </div>
  );
}

// ============================================
// TAB COMPONENTS
// ============================================

function OverviewTab({ caseData }) {
  return (
    <div className="tab-content overview-tab">
      <div className="overview-grid">
        {/* Case Info Card */}
        <div className="info-card">
          <h3>Case Information</h3>
          <div className="info-rows">
            <div className="info-row">
              <span className="label">Description</span>
              <span className="value">{caseData.description || 'No description'}</span>
            </div>
            <div className="info-row">
              <span className="label">Opened</span>
              <span className="value">{formatDateTime(caseData.opened_at)}</span>
            </div>
            {caseData.due_at && (
              <div className="info-row">
                <span className="label">Due Date</span>
                <span className={`value ${caseData.is_overdue ? 'overdue' : ''}`}>
                  {formatDateTime(caseData.due_at)}
                  {caseData.is_overdue && ' (Overdue)'}
                </span>
              </div>
            )}
            {caseData.resolved_at && (
              <div className="info-row">
                <span className="label">Resolved</span>
                <span className="value">{formatDateTime(caseData.resolved_at)}</span>
              </div>
            )}
            {caseData.resolution && (
              <div className="info-row">
                <span className="label">Resolution</span>
                <span className={`badge ${resolutionConfig[caseData.resolution]?.color || 'muted'}`}>
                  {resolutionConfig[caseData.resolution]?.label || caseData.resolution}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Assignment Card */}
        <div className="info-card">
          <h3>Assignment</h3>
          <div className="info-rows">
            <div className="info-row">
              <span className="label">Assignee</span>
              <span className="value">
                {caseData.assignee ? (
                  <span className="user-badge">
                    <User size={14} />
                    {caseData.assignee.name || caseData.assignee.email}
                  </span>
                ) : (
                  <span className="muted">Unassigned</span>
                )}
              </span>
            </div>
            {caseData.escalation_level > 0 && (
              <>
                <div className="info-row">
                  <span className="label">Escalation Level</span>
                  <span className="value badge warning">Level {caseData.escalation_level}</span>
                </div>
                {caseData.escalated_to && (
                  <div className="info-row">
                    <span className="label">Escalated To</span>
                    <span className="value">
                      <span className="user-badge">
                        <User size={14} />
                        {caseData.escalated_to.name || caseData.escalated_to.email}
                      </span>
                    </span>
                  </div>
                )}
                {caseData.escalation_reason && (
                  <div className="info-row">
                    <span className="label">Escalation Reason</span>
                    <span className="value">{caseData.escalation_reason}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* SLA Metrics Card */}
        <div className="info-card">
          <h3>SLA Metrics</h3>
          <div className="info-rows">
            <div className="info-row">
              <span className="label">Time to First Response</span>
              <span className="value">
                {caseData.time_to_first_response_minutes
                  ? `${caseData.time_to_first_response_minutes} min`
                  : '—'}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Time to Resolution</span>
              <span className="value">
                {caseData.time_to_resolution_minutes
                  ? `${caseData.time_to_resolution_minutes} min`
                  : '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Screening Hit Card (if exists) */}
        {caseData.screening_hit && (
          <div className="info-card">
            <h3>Screening Hit</h3>
            <div className="info-rows">
              <div className="info-row">
                <span className="label">Match Name</span>
                <span className="value">{caseData.screening_hit.match_name || '—'}</span>
              </div>
              <div className="info-row">
                <span className="label">Match Score</span>
                <span className="value">
                  {caseData.screening_hit.match_score
                    ? `${(caseData.screening_hit.match_score * 100).toFixed(0)}%`
                    : '—'}
                </span>
              </div>
              <div className="info-row">
                <span className="label">List</span>
                <span className="value">{caseData.screening_hit.list_name || '—'}</span>
              </div>
              <div className="info-row">
                <span className="label">Status</span>
                <span className={`badge ${caseData.screening_hit.status === 'resolved' ? 'success' : 'warning'}`}>
                  {caseData.screening_hit.status}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SubjectTab({ caseData }) {
  const applicant = caseData.applicant;
  const documents = caseData.documents || [];

  if (!applicant) {
    return (
      <div className="tab-content subject-tab">
        <div className="empty-state">
          <User size={48} />
          <h3>No Subject Linked</h3>
          <p>This case doesn't have a linked applicant or company.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content subject-tab">
      {/* Applicant Card */}
      <div className="info-card applicant-card">
        <div className="card-header">
          <h3>Applicant</h3>
          <Link to={`/applicants/${applicant.id}`} className="btn btn-sm btn-outline">
            <ExternalLink size={14} />
            View Full Profile
          </Link>
        </div>
        <div className="applicant-info">
          <div className="applicant-avatar">
            <User size={32} />
          </div>
          <div className="applicant-details">
            <div className="name">
              {applicant.first_name && applicant.last_name
                ? `${applicant.first_name} ${applicant.last_name}`
                : applicant.external_id || 'Unknown'}
            </div>
            {applicant.email && <div className="email">{applicant.email}</div>}
            <div className="meta">
              <span className={`badge ${applicant.status === 'approved' ? 'success' : applicant.status === 'rejected' ? 'danger' : 'muted'}`}>
                {applicant.status}
              </span>
              {applicant.risk_level && (
                <span className={`badge ${applicant.risk_level === 'low' ? 'success' : applicant.risk_level === 'high' ? 'danger' : 'warning'}`}>
                  {applicant.risk_level} risk
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="info-card">
        <h3>Documents ({documents.length})</h3>
        {documents.length === 0 ? (
          <p className="muted">No documents uploaded</p>
        ) : (
          <div className="documents-list">
            {documents.map((doc) => (
              <div key={doc.id} className="document-item">
                <FileText size={16} />
                <div className="doc-info">
                  <div className="doc-type">{doc.type}</div>
                  <div className="doc-name">{doc.file_name || 'Document'}</div>
                </div>
                <span className={`badge ${doc.status === 'verified' ? 'success' : doc.status === 'rejected' ? 'danger' : 'muted'}`}>
                  {doc.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InvestigationTab({ caseData, timeline, noteText, setNoteText, onAddNote, isAddingNote }) {
  const notes = caseData.notes || [];

  return (
    <div className="tab-content investigation-tab">
      <div className="investigation-grid">
        {/* Notes Section */}
        <div className="info-card notes-card">
          <h3>Notes ({notes.length})</h3>

          {/* Add Note Form */}
          <div className="add-note-form">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note..."
              rows={3}
            />
            <button
              className="btn btn-primary btn-sm"
              onClick={onAddNote}
              disabled={!noteText.trim() || isAddingNote}
            >
              {isAddingNote ? <Loader2 className="spinner" size={14} /> : <Send size={14} />}
              Add Note
            </button>
          </div>

          {/* Notes List */}
          <div className="notes-list">
            {notes.length === 0 ? (
              <p className="muted">No notes yet</p>
            ) : (
              notes.map((note) => (
                <div key={note.id} className="note-item">
                  <div className="note-header">
                    <span className="note-author">
                      {note.author ? (note.author.name || note.author.email) : 'System'}
                      {note.is_ai_generated && <span className="ai-badge">AI</span>}
                    </span>
                    <span className="note-time">{formatRelativeTime(note.created_at)}</span>
                  </div>
                  <div className="note-content">{note.content}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Timeline Section */}
        <div className="info-card timeline-card">
          <h3>
            <History size={18} />
            Activity Timeline
          </h3>
          <CaseTimeline events={timeline?.items || []} />
        </div>
      </div>
    </div>
  );
}

function TagsTab({ caseTags, availableTags, onAddTag, onRemoveTag }) {
  return (
    <div className="tab-content tags-tab">
      <div className="info-card">
        <h3>Assigned Tags ({caseTags.length})</h3>
        <div className="tags-list">
          {caseTags.length === 0 ? (
            <p className="muted">No tags assigned</p>
          ) : (
            caseTags.map((tag) => (
              <div key={tag.id} className="tag-chip" style={{ backgroundColor: tag.color + '20', borderColor: tag.color }}>
                <span className="tag-dot" style={{ backgroundColor: tag.color }} />
                {tag.name}
                <button className="tag-remove" onClick={() => onRemoveTag(tag.id)}>
                  <X size={12} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {availableTags.length > 0 && (
        <div className="info-card">
          <h3>Available Tags</h3>
          <div className="tags-list">
            {availableTags.map((tag) => (
              <button
                key={tag.id}
                className="tag-chip clickable"
                style={{ backgroundColor: tag.color + '20', borderColor: tag.color }}
                onClick={() => onAddTag(tag.id)}
              >
                <Plus size={12} />
                {tag.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// RESOLVE MODAL
// ============================================

function ResolveModal({ onClose, onResolve, isLoading }) {
  const [resolution, setResolution] = useState('cleared');
  const [notes, setNotes] = useState('');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Resolve Case</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Resolution</label>
            <select value={resolution} onChange={(e) => setResolution(e.target.value)}>
              <option value="cleared">Cleared - No issue found</option>
              <option value="confirmed">Confirmed - Issue verified</option>
              <option value="reported">Reported - Filed with authorities</option>
              <option value="escalated">Escalated - Sent for further review</option>
            </select>
          </div>
          <div className="form-group">
            <label>Resolution Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about the resolution..."
              rows={4}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => onResolve(resolution, notes)}
            disabled={isLoading}
          >
            {isLoading ? <Loader2 className="spinner" size={16} /> : <CheckCircle2 size={16} />}
            Resolve Case
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// STYLES
// ============================================

const styles = `
.case-detail-page {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
  color: var(--text-secondary);
}

.loading-state .spinner {
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-state svg {
  color: var(--color-warning);
  margin-bottom: 16px;
}

.error-state h2 {
  margin: 0 0 8px;
  color: var(--text-primary);
}

.error-state p {
  margin: 0 0 24px;
}

/* Header */
.case-header {
  margin-bottom: 24px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 14px;
  margin-bottom: 16px;
}

.back-btn:hover {
  color: var(--text-primary);
}

.case-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
}

.case-number {
  font-size: 12px;
  color: var(--text-secondary);
  font-family: var(--font-mono);
}

.case-title-info h1 {
  margin: 4px 0 12px;
  font-size: 24px;
  font-weight: 600;
}

.case-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.badge.success { background: var(--color-success-bg); color: var(--color-success); }
.badge.warning { background: var(--color-warning-bg); color: var(--color-warning); }
.badge.danger { background: var(--color-danger-bg); color: var(--color-danger); }
.badge.info { background: var(--color-info-bg); color: var(--color-info); }
.badge.muted { background: var(--bg-tertiary); color: var(--text-secondary); }

.case-actions-area {
  display: flex;
  align-items: flex-end;
  gap: 24px;
}

.assignee-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.assignee-label {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

.case-actions {
  display: flex;
  gap: 12px;
}

/* Tabs */
.case-tabs {
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 24px;
}

.tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.tab:hover {
  color: var(--text-primary);
  background: var(--bg-secondary);
}

.tab.active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
}

/* Content */
/* Layout with optional sidebar */
.case-layout {
  display: flex;
  gap: 24px;
}

.case-layout .case-content {
  flex: 1;
  min-width: 0;
}

.case-layout.with-sidebar .case-content {
  flex: 1;
}

.case-sidebar {
  width: 320px;
  flex-shrink: 0;
}

@media (max-width: 1100px) {
  .case-layout {
    flex-direction: column;
  }

  .case-sidebar {
    width: 100%;
    order: -1;
  }
}

.tab-content {
  min-height: 400px;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

@media (max-width: 900px) {
  .overview-grid {
    grid-template-columns: 1fr;
  }
}

.info-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 20px;
}

.info-card h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.card-header h3 {
  margin: 0;
}

.info-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.info-row .label {
  font-size: 13px;
  color: var(--text-secondary);
  flex-shrink: 0;
}

.info-row .value {
  font-size: 13px;
  color: var(--text-primary);
  text-align: right;
}

.info-row .value.overdue {
  color: var(--color-danger);
}

.user-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.muted {
  color: var(--text-secondary);
}

/* Subject Tab */
.applicant-card .applicant-info {
  display: flex;
  gap: 16px;
  align-items: center;
}

.applicant-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--bg-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
}

.applicant-details .name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
}

.applicant-details .email {
  font-size: 13px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.applicant-details .meta {
  display: flex;
  gap: 8px;
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.document-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--bg-secondary);
  border-radius: 8px;
}

.document-item .doc-info {
  flex: 1;
}

.document-item .doc-type {
  font-size: 13px;
  font-weight: 500;
}

.document-item .doc-name {
  font-size: 12px;
  color: var(--text-secondary);
}

/* Investigation Tab */
.investigation-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

@media (max-width: 900px) {
  .investigation-grid {
    grid-template-columns: 1fr;
  }
}

.add-note-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.add-note-form textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  resize: vertical;
}

.add-note-form .btn {
  align-self: flex-end;
}

.notes-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
}

.note-item {
  padding: 12px;
  background: var(--bg-secondary);
  border-radius: 8px;
}

.note-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.note-author {
  font-size: 12px;
  font-weight: 500;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.ai-badge {
  padding: 2px 6px;
  background: var(--color-info-bg);
  color: var(--color-info);
  border-radius: 4px;
  font-size: 10px;
}

.note-time {
  font-size: 11px;
  color: var(--text-secondary);
}

.note-content {
  font-size: 13px;
  color: var(--text-primary);
  line-height: 1.5;
  white-space: pre-wrap;
}

/* Tags Tab */
.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid;
  border-radius: 16px;
  font-size: 13px;
}

.tag-chip.clickable {
  cursor: pointer;
  transition: transform 0.1s;
}

.tag-chip.clickable:hover {
  transform: scale(1.02);
}

.tag-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.tag-remove {
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
}

.tag-remove:hover {
  opacity: 1;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  color: var(--text-secondary);
}

.empty-state svg {
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  margin: 0 0 8px;
  color: var(--text-primary);
}

.empty-state p {
  margin: 0;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-primary {
  background: var(--color-primary);
  color: white;
}

.btn-primary:hover {
  background: var(--color-primary-hover);
}

.btn-warning {
  background: var(--color-warning);
  color: white;
}

.btn-warning:hover {
  background: #d97706;
}

.btn-outline {
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}

.btn-outline:hover {
  background: var(--bg-secondary);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--text-secondary);
}

.close-btn:hover {
  color: var(--text-primary);
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
}

.form-group select,
.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid var(--border-color);
}
`;
